import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { Box, Typography } from '@mui/material'
import React from 'react'
import { ChartView } from 'src/Libs/chart-kit'
import LegendItem, { LegendItemProps } from 'src/Libs/chart-kit/shared/components/LegendItem'
import { makeStyles } from 'tss-react/mui'
import { useCostSavingChartColorSettings } from '../../hooks/use-cost-saving-chart-color-settings'
import { EmsBatteryMode } from '../../types'
import { HIDDEN_TRACE_KEYS_FOR_DAY_VIEW } from './ChartContainer'
import { CostSavingChartTraceKey, CostSavingChartTraceKeys } from '../../cost-saving-chart-trace-keys'

export type LegendItemConfig = LegendItemProps & {
  traceKey: CostSavingChartTraceKey
}
export interface Props {
  hiddenTraces: CostSavingChartTraceKey[]
  toggleTraceVisibility: (trace: CostSavingChartTraceKey) => void
  view: ChartView
  showDynamicEmsLegend: boolean | undefined
}

const useStyles = makeStyles()(theme => ({
  legendItemsWrapper: {
    display: 'grid',
    gridTemplateColumns: `repeat(auto-fit, minmax(120px, max-content))`,
    gap: theme.spacing(4),
    width: '100%',
  },
  dynamicEmsLegendItemsContainer: {
    marginTop: theme.spacing(4),

    '& > span:first-of-type': {
      display: 'block',
      marginTop: theme.spacing(1),
      marginBottom: theme.spacing(2),

      [theme.breakpoints.up('sm')]: {
        margin: 0,
        minWidth: 'max-content',
        marginRight: theme.spacing(4),
      },
    },

    [theme.breakpoints.up('sm')]: {
      display: 'flex',

      '& > div:first-of-type': {
        display: 'flex',
      },
    },
  },
}))

/**
 *  Legend component for the cost savings chart
 */
const Legend: React.FC<Props> = ({ view, hiddenTraces, showDynamicEmsLegend, toggleTraceVisibility }) => {
  const { t } = useCustomTranslation()
  const { classes } = useStyles()
  const chartColor = useCostSavingChartColorSettings()

  const legendItems: LegendItemConfig[] = [
    {
      type: 'bar',
      traceKey: CostSavingChartTraceKeys.ThreeSixtyBatterySavings,
      title: t('dynamic-power-prices.cost-saving-chart.legend.three-sixty-savings-hps'),
      color: chartColor.threeSixtySavingsBatteryColor,
    },
    {
      type: 'bar',
      traceKey: CostSavingChartTraceKeys.ThreeSixtyWallboxSavings,
      title: t('dynamic-power-prices.cost-saving-chart.legend.three-sixty-savings-wallbox'),
      color: chartColor.threeSixtySavingsWallboxColor,
    },
    {
      type: 'line',
      traceKey: CostSavingChartTraceKeys.ConsumptionPrice,
      title: t('dynamic-power-prices.cost-saving-chart.legend.consumption-price'),
      color: chartColor.consumptionPriceColor,
    },
  ]

  const filteredLegendItems = legendItems.filter(label => (view === 'day' ? !HIDDEN_TRACE_KEYS_FOR_DAY_VIEW.includes(label.traceKey) : true))

  return (
    <Box sx={{ ml: { xs: 0, sm: 2 } }}>
      <Box className={classes.legendItemsWrapper}>
        {filteredLegendItems.map(({ traceKey, ...rest }) => (
          <LegendItem key={traceKey} isDisabled={hiddenTraces.includes(traceKey)} onClick={() => toggleTraceVisibility(traceKey)} {...rest} />
        ))}
      </Box>
      {showDynamicEmsLegend && <DynamicEmsLegendItems hiddenTraces={hiddenTraces} toggleTraceVisibility={toggleTraceVisibility} />}
    </Box>
  )
}

export default Legend

/**
 * Dynamic EMS legend items
 * */
const DynamicEmsLegendItems: React.FC<Omit<Props, 'view' | 'showDynamicEmsLegend'>> = ({ hiddenTraces, toggleTraceVisibility }) => {
  const { t } = useCustomTranslation()
  const chartColor = useCostSavingChartColorSettings()
  const { classes } = useStyles()

  const legendItems: LegendItemConfig[] = [
    {
      type: 'gradient',
      traceKey: EmsBatteryMode.NormalOperation,
      title: t('dynamic-ems.cost-saving-chart.legend.normal-operation'),
      color: chartColor.dynamicEmsNormalOperationColor,
    },
    {
      type: 'gradient',
      traceKey: EmsBatteryMode.BlockDischarging,
      title: t('dynamic-ems.cost-saving-chart.legend.block-discharging'),
      color: chartColor.dynamicEmsBlockDischargingColor,
    },
    {
      type: 'gradient',
      traceKey: EmsBatteryMode.GridCharge,
      title: t('dynamic-ems.cost-saving-chart.legend.grid-charge'),
      color: chartColor.dynamicEmsGridChargeColor,
    },
  ]

  return (
    <Box className={classes.dynamicEmsLegendItemsContainer}>
      <Typography variant="caption">{t('dynamic-ems.cost-saving-chart.legend.description')}</Typography>
      <Box className={classes.legendItemsWrapper}>
        {legendItems.map(({ traceKey, ...rest }) => (
          <LegendItem key={traceKey} isDisabled={hiddenTraces.includes(traceKey)} onClick={() => toggleTraceVisibility(traceKey)} {...rest} />
        ))}
      </Box>
    </Box>
  )
}
